import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, ListChecks, Zap, CheckCircle2, Upload, Check, CalendarPlus, Sparkles, Send, Repeat, CalendarClock, Pencil, Trash2, Info, AlertTriangle } from "lucide-react";
import { BrutalButton, SectionTitle, EmptyState } from "@/components/Brutal";
import TaskCard from "@/components/TaskCard";
import TaskWizard from "@/components/TaskWizard";
import QuickTileDialog from "@/components/QuickTileDialog";
import TaskImportWizard from "@/components/TaskImportWizard";
import { DUE_OPTIONS, effectiveDagdeel, taskOverdueInfo, localISO, taskEffDate } from "@/lib/labels";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";

const TILE_BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Taken() {
  const { user } = useAuth();
  const t = useT();
  const [tasks, setTasks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [tiles, setTiles] = useState([]);
  const [wizard, setWizard] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [tile, setTile] = useState(null);
  const [view, setView] = useState("open");
  const [searchParams] = useSearchParams();
  const [scope, setScope] = useState(searchParams.get("scope") === "mij" ? "mij" : "alle");
  const [dateFilter, setDateFilter] = useState("vandaag");
  const [dagdeelFilter, setDagdeelFilter] = useState("alle");
  const admin = isAdmin(user);
  const [showLegend, setShowLegend] = useState(false);
  const [flashId, setFlashId] = useState(null);
  const highlightId = searchParams.get("task");
  useEffect(() => {
    // Vanuit de Dashboard-tegels ("Openstaande taken" → scope=alle, "Voor mij" → scope=mij):
    // toon álle openstaande taken (over alle datums), niet alleen die van vandaag.
    const sp = searchParams.get("scope");
    if (sp === "mij" || sp === "alle") {
      setScope(sp); setView("open"); setDateFilter("alle");
    }
  }, [searchParams]);
  useEffect(() => {
    if (!highlightId || !tasks.length) return;
    setView("open"); setDateFilter("alle"); setScope("alle");
    const to = setTimeout(() => {
      const el = document.getElementById(`task-${highlightId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      setFlashId(highlightId);
    }, 350);
    const clr = setTimeout(() => setFlashId(null), 3500);
    return () => { clearTimeout(to); clearTimeout(clr); };
  }, [highlightId, tasks.length]);

  const legendItems = [
    { icon: Check, bg: "bg-brand-green", label: "Afvinken — taak op klaar zetten" },
    { icon: CalendarPlus, bg: "bg-brand-teal text-white", label: "In je agenda zetten (Apple/Google/Outlook)" },
    ...(admin ? [{ icon: Sparkles, bg: "bg-brand-violet text-white", label: "AI wijst eerlijk iemand aan (beheerder)" }] : []),
    { icon: Send, bg: "bg-brand-blue text-white", label: "Overdragen aan een huisgenoot" },
    { icon: Repeat, bg: "bg-brand-violet text-white", label: "Ruilen / teruggeven op de ruilmarkt" },
    { icon: CalendarClock, bg: "bg-brand-yellow", label: "Uitstellen naar een andere dag" },
    ...(admin ? [{ icon: CheckCircle2, bg: "bg-brand-green", label: "Bevestigen na afvinken (beheerder)" }] : []),
    { icon: Pencil, bg: "bg-brand-yellow", label: "Bewerken" },
    { icon: Trash2, bg: "bg-white", label: "Verwijderen" },
  ];

  const load = async () => {
    const [t, g, u, q] = await Promise.all([api.get("/tasks"), api.get("/groups"), api.get("/users"), api.get("/tasks/quick-tiles")]);
    setTasks(t.data); setGroups(g.data); setUsers(u.data); setTiles(q.data);
  };
  useEffect(() => { load(); }, []);

  const isMine = (t) => {
    const ids = t.assignee_ids?.length ? t.assignee_ids : (t.assignee_id ? [t.assignee_id] : []);
    return ids.includes(user?.user_id);
  };
  const openTasks = tasks.filter((t) => t.status !== "klaar" && t.status !== "wacht_op_bevestiging");
  const done = tasks.filter((t) => t.status === "klaar" || t.status === "wacht_op_bevestiging").sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""));

  const iso = (d) => localISO(d);
  const todayISO = iso(new Date());
  const tomorrowISO = iso(new Date(Date.now() + 864e5));
  const dayAfterISO = iso(new Date(Date.now() + 2 * 864e5));
  const weekEndISO = iso(new Date(Date.now() + 7 * 864e5));
  const dateFor = (t) => taskEffDate(t) || todayISO;

  const withOv = openTasks.map((t) => ({ t, ov: taskOverdueInfo(t) }));
  const filtered = withOv.filter(({ t, ov }) => {
    const d = dateFor(t);
    const dateOk = dateFilter === "alle" ? true
      : dateFilter === "vandaag" ? (d === todayISO || ov.overdue)
      : dateFilter === "morgen" ? d === tomorrowISO
      : dateFilter === "overmorgen" ? d === dayAfterISO
      : (d >= todayISO && d < weekEndISO) || ov.overdue;
    const ddOk = dagdeelFilter === "alle" || effectiveDagdeel(t) === dagdeelFilter;
    const scopeOk = scope === "alle" || isMine(t);
    return dateOk && ddOk && scopeOk;
  }).sort((a, b) => {
    if (a.ov.overdue !== b.ov.overdue) return a.ov.overdue ? -1 : 1;
    if (a.ov.overdue && b.ov.overdue) return b.ov.daysOver - a.ov.daysOver;
    return (a.t.next_date || todayISO).localeCompare(b.t.next_date || todayISO);
  }).map(({ t }) => t);
  const overdueCount = filtered.filter((t) => taskOverdueInfo(t).overdue).length;

  const batchMap = {};
  tasks.forEach((tk) => { if (tk.batch_id) (batchMap[tk.batch_id] = batchMap[tk.batch_id] || []).push(tk); });
  const batchFor = (tk) => (tk.batch_id && (batchMap[tk.batch_id] || []).length > 1) ? batchMap[tk.batch_id] : null;

  const dateFilters = [
    { value: "vandaag", label: "Vandaag" }, { value: "morgen", label: "Morgen" },
    { value: "overmorgen", label: "Overmorgen" }, { value: "week", label: "Deze week" }, { value: "alle", label: "Alle" },
  ];
  const dagdeelFilters = [
    { value: "alle", label: "Hele dag" }, { value: "ochtend", label: "Ochtend" },
    { value: "middag", label: "Middag" }, { value: "avond", label: "Avond" },
  ];

  return (
    <div className="space-y-5" data-testid="taken-page">
      <div className="flex items-center justify-between">
        <SectionTitle sub={`${openTasks.length} open · ${done.length} afgerond`}>Taken</SectionTitle>
        <div className="flex gap-2">
          {isAdmin(user) && <BrutalButton data-testid="import-roster-btn" variant="pink" onClick={() => setImportOpen(true)}><Upload className="w-5 h-5" strokeWidth={3} /><span className="hidden sm:inline">Importeren</span></BrutalButton>}
          <BrutalButton data-testid="new-task-btn" onClick={() => setWizard(true)}><Plus className="w-5 h-5" strokeWidth={3} />Nieuw</BrutalButton>
        </div>
      </div>

      <TaskImportWizard open={importOpen} onClose={() => setImportOpen(false)} onDone={load} users={users} groups={groups} />

      {tiles.length > 0 && (
        <div>
          <p className="font-head font-bold text-sm mb-2 flex items-center gap-1.5"><Zap className="w-4 h-4 text-brand-violet" strokeWidth={2.5} />Quick Tiles</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {tiles.map((t) => (
              <button key={t.id} data-testid={`taken-tile-${t.id}`} onClick={() => setTile(t)}
                className="brutal-sm brutal-press rounded-xl bg-brand-yellow pl-1.5 pr-3 py-1.5 font-head font-bold text-sm flex items-center gap-2 text-left">
                {t.image_url
                  ? <img src={t.image_url.startsWith("http") ? t.image_url : `${TILE_BACKEND}${t.image_url}`} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0 bg-white" />
                  : <span className="w-9 h-9 rounded-lg bg-white/60 flex items-center justify-center shrink-0"><Zap className="w-4 h-4" strokeWidth={2.5} /></span>}
                <span className="truncate flex-1">{t.title}</span>
                <Zap className="w-3.5 h-3.5 shrink-0 opacity-50" strokeWidth={2.5} />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button data-testid="view-open" onClick={() => setView("open")}
          className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold flex items-center gap-1.5 ${view === "open" ? "bg-brand-ink text-white" : "bg-white"}`}>
          <ListChecks className="w-4 h-4" strokeWidth={2.5} />{t("Open")} ({openTasks.length})
        </button>
        <button data-testid="view-afgerond" onClick={() => setView("afgerond")}
          className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold flex items-center gap-1.5 ${view === "afgerond" ? "bg-brand-ink text-white" : "bg-white"}`}>
          <CheckCircle2 className="w-4 h-4" strokeWidth={2.5} />{t("Afgerond")} ({done.length})
        </button>
        <button data-testid="legend-toggle" onClick={() => setShowLegend((v) => !v)} title={t("Wat betekenen de knoppen?")}
          className={`brutal-sm brutal-press rounded-full px-3 py-2 text-sm font-bold flex items-center gap-1.5 ml-auto ${showLegend ? "bg-brand-violet text-white" : "bg-white"}`}>
          <Info className="w-4 h-4" strokeWidth={2.5} />{t("Legenda")}
        </button>
      </div>

      {showLegend && (
        <div data-testid="legend-card" className="brutal rounded-xl bg-white p-4">
          <p className="font-head font-black text-base mb-3">{t("Wat betekenen de knoppen?")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
            {legendItems.map((it, i) => {
              const Ic = it.icon;
              return (
                <div key={i} className="flex items-center gap-2.5">
                  <span className={`shrink-0 brutal-sm rounded-full p-1.5 flex items-center justify-center ${it.bg}`}><Ic className="w-4 h-4" strokeWidth={2.5} /></span>
                  <span className="text-sm font-semibold text-brand-ink/80">{t(it.label)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "open" && (
        <>
          <div className="flex gap-2">
            <button data-testid="scope-mij" onClick={() => setScope("mij")}
              className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold ${scope === "mij" ? "bg-brand-blue text-white" : "bg-white"}`}>{t("Voor mij")}</button>
            <button data-testid="scope-alle" onClick={() => setScope("alle")}
              className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold ${scope === "alle" ? "bg-brand-blue text-white" : "bg-white"}`}>{t("Iedereen")}</button>
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {dateFilters.map((f) => (
              <button key={f.value} data-testid={`date-filter-${f.value}`} onClick={() => setDateFilter(f.value)}
                className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold whitespace-nowrap ${dateFilter === f.value ? "bg-brand-violet text-white" : "bg-white"}`}>{t(f.label)}</button>
            ))}
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {dagdeelFilters.map((f) => (
              <button key={f.value} data-testid={`dagdeel-filter-${f.value}`} onClick={() => setDagdeelFilter(f.value)}
                className={`brutal-sm brutal-press rounded-full px-3 py-1.5 text-xs font-bold whitespace-nowrap ${dagdeelFilter === f.value ? "bg-brand-ink text-white" : "bg-white"}`}>{t(f.label)}</button>
            ))}
          </div>
          {filtered.length ? (
            <div className="space-y-3">
              {overdueCount > 0 && (
                <div data-testid="overdue-counter" className="flex items-center gap-2 bg-red-500 text-white brutal-sm rounded-full px-4 py-2 font-head font-bold text-sm w-fit">
                  <AlertTriangle className="w-4 h-4" strokeWidth={2.5} />{overdueCount} {t("te laat")}
                </div>
              )}
              {filtered.map((t) => (
                <div key={t.id} id={`task-${t.id}`} className={flashId === t.id ? "rounded-2xl ring-4 ring-brand-violet ring-offset-2 transition-all" : "transition-all"}>
                  <TaskCard task={t} users={users} groups={groups} onChange={load} onEdit={setEditTask} batch={batchFor(t)} />
                </div>
              ))}
            </div>
          ) : <EmptyState icon={ListChecks} title="Geen open taken" text="Maak een taak aan of gebruik een Quick Tile." />}
        </>
      )}

      {view === "afgerond" && (
        done.length ? (
          <div className="space-y-3" data-testid="afgerond-list">
            {done.map((t) => <TaskCard key={t.id} task={t} users={users} groups={groups} onChange={load} onEdit={setEditTask} batch={batchFor(t)} />)}
          </div>
        ) : <EmptyState icon={CheckCircle2} title={t("Nog niks afgerond")} text={t("Afgeronde taken verschijnen hier.")} />
      )}

      {wizard && <TaskWizard groups={groups} users={users} onClose={() => setWizard(false)} onCreated={load} />}
      {editTask && <TaskWizard task={editTask} groups={groups} users={users} onClose={() => setEditTask(null)} onCreated={load} />}
      {tile && <QuickTileDialog tile={tile} users={users} admin={isAdmin(user)} onEdit={(t) => { setTile(null); setEditTask(t); }} onClose={() => setTile(null)} onDone={load} />}
    </div>
  );
}
